import type { CountryCode, CountryConfig, CountryLite, NavItem } from './types';
import {
  IconBriefcase, IconCalendarCheck, IconReceipt, IconBankColumns,
  IconStackedCoins, IconOfficeBuilding, IconClipboardSign, IconStatHoliday,
} from './icons';

export const API_URL: string = process.env.REACT_APP_API_URL || 'https://api.getnovala.com';
export const FONT = 'Inter, system-ui, sans-serif';

export const COUNTRIES: ReadonlyArray<CountryLite> = [
  { iso: 'us', name: 'United States', currency: 'USD' },
  { iso: 'ca', name: 'Canada', currency: 'CAD' },
  { iso: 'gb', name: 'United Kingdom', currency: 'GBP' },
  { iso: 'au', name: 'Australia', currency: 'AUD' },
  { iso: 'nz', name: 'New Zealand', currency: 'NZD' },
  { iso: 'sg', name: 'Singapore', currency: 'SGD' },
  { iso: 'jp', name: 'Japan', currency: 'JPY' },
  { iso: 'de', name: 'Germany', currency: 'EUR' },
  { iso: 'fr', name: 'France', currency: 'EUR' },
  { iso: 'za', name: 'South Africa', currency: 'ZAR' },
];

export const SECTIONS: ReadonlyArray<NavItem> = [
  { id: 'company', group: 'Setup', label: 'Company details', Icon: IconBriefcase },
  { id: 'schedule', group: 'Setup', label: 'Default pay schedule', Icon: IconCalendarCheck },
  { id: 'tax', group: 'Setup', label: 'Tax registration', Icon: IconReceipt },
  { id: 'bank', group: 'Setup', label: 'Bank account', Icon: IconBankColumns },
  { id: 'items', group: 'Payroll items', label: 'Pay types & deductions', Icon: IconStackedCoins },
  { id: 'locations', group: 'Payroll items', label: 'Work locations', Icon: IconOfficeBuilding },
  { id: 'stat_holiday', group: 'Payroll items', label: 'Stat holiday method', Icon: IconStatHoliday },
  { id: 'review', group: 'Final step', label: 'Review & authorize', Icon: IconClipboardSign, comingSoon: true },
];

export const COUNTRY_CONFIG: Record<CountryCode, CountryConfig> = {
  CA: {
    name: 'Canada', iso: 'ca', subdivLabel: 'Province or territory',
    subdivisions: ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Nova Scotia','Ontario','Prince Edward Island','Quebec','Saskatchewan','Northwest Territories','Nunavut','Yukon'],
    postalLabel: 'Postal code', postalFormat: 'A1A 1A1', postalPlaceholder: 'T5J 3K1',
    streetPlaceholder: '10145 109 Street NW', cityPlaceholder: 'Edmonton', phonePlaceholder: '+1 (780) 555-0100',
  },
  US: {
    name: 'United States', iso: 'us', subdivLabel: 'State',
    subdivisions: ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'],
    postalLabel: 'ZIP code', postalFormat: '12345', postalPlaceholder: '94103',
    streetPlaceholder: '1455 Market Street', cityPlaceholder: 'San Francisco', phonePlaceholder: '+1 (415) 555-0100',
  },
  GB: {
    name: 'United Kingdom', iso: 'gb', subdivLabel: 'Nation',
    subdivisions: ['England','Scotland','Wales','Northern Ireland'],
    postalLabel: 'Postcode', postalFormat: 'SW1A 1AA', postalPlaceholder: 'SW1A 1AA',
    streetPlaceholder: '10 Downing Street', cityPlaceholder: 'London', phonePlaceholder: '+44 20 7946 0100',
  },
  AU: {
    name: 'Australia', iso: 'au', subdivLabel: 'State or territory',
    subdivisions: ['Australian Capital Territory','New South Wales','Northern Territory','Queensland','South Australia','Tasmania','Victoria','Western Australia'],
    postalLabel: 'Postcode', postalFormat: '4 digits', postalPlaceholder: '2000',
    streetPlaceholder: '1 Macquarie Street', cityPlaceholder: 'Sydney', phonePlaceholder: '+61 2 9374 4000',
  },
  NZ: {
    name: 'New Zealand', iso: 'nz', subdivLabel: 'Region',
    subdivisions: ['Auckland','Bay of Plenty','Canterbury','Gisborne',"Hawke's Bay",'Manawatu-Whanganui','Marlborough','Nelson','Northland','Otago','Southland','Taranaki','Tasman','Waikato','Wellington','West Coast'],
    postalLabel: 'Postcode', postalFormat: '4 digits', postalPlaceholder: '1010',
    streetPlaceholder: '1 Queen Street', cityPlaceholder: 'Auckland', phonePlaceholder: '+64 9 379 2020',
  },
  SG: {
    name: 'Singapore', iso: 'sg', subdivLabel: 'Region (optional)',
    subdivisions: ['Central Region','East Region','North Region','North-East Region','West Region'],
    postalLabel: 'Postal code', postalFormat: '6 digits', postalPlaceholder: '238859',
    streetPlaceholder: '1 Marina Boulevard', cityPlaceholder: 'Singapore', phonePlaceholder: '+65 6688 1234',
  },
  JP: {
    name: 'Japan', iso: 'jp', subdivLabel: 'Prefecture',
    subdivisions: ['Hokkaido','Aomori','Iwate','Miyagi','Akita','Yamagata','Fukushima','Ibaraki','Tochigi','Gunma','Saitama','Chiba','Tokyo','Kanagawa','Niigata','Toyama','Ishikawa','Fukui','Yamanashi','Nagano','Gifu','Shizuoka','Aichi','Mie','Shiga','Kyoto','Osaka','Hyogo','Nara','Wakayama','Tottori','Shimane','Okayama','Hiroshima','Yamaguchi','Tokushima','Kagawa','Ehime','Kochi','Fukuoka','Saga','Nagasaki','Kumamoto','Oita','Miyazaki','Kagoshima','Okinawa'],
    postalLabel: 'Postal code', postalFormat: 'XXX-XXXX', postalPlaceholder: '100-0001',
    streetPlaceholder: '1-1 Chiyoda', cityPlaceholder: 'Chiyoda City, Tokyo', phonePlaceholder: '+81 3 1234 5678',
  },
  DE: {
    name: 'Germany', iso: 'de', subdivLabel: 'State (Bundesland)',
    subdivisions: ['Baden-Württemberg','Bayern','Berlin','Brandenburg','Bremen','Hamburg','Hessen','Mecklenburg-Vorpommern','Niedersachsen','Nordrhein-Westfalen','Rheinland-Pfalz','Saarland','Sachsen','Sachsen-Anhalt','Schleswig-Holstein','Thüringen'],
    postalLabel: 'PLZ', postalFormat: '5 digits', postalPlaceholder: '10115',
    streetPlaceholder: 'Unter den Linden 77', cityPlaceholder: 'Berlin', phonePlaceholder: '+49 30 12345678',
  },
  FR: {
    name: 'France', iso: 'fr', subdivLabel: 'Region',
    subdivisions: ['Auvergne-Rhône-Alpes','Bourgogne-Franche-Comté','Bretagne','Centre-Val de Loire','Corse','Grand Est','Hauts-de-France','Île-de-France','Normandie','Nouvelle-Aquitaine','Occitanie','Pays de la Loire',"Provence-Alpes-Côte d'Azur"],
    postalLabel: 'Code postal', postalFormat: '5 digits', postalPlaceholder: '75001',
    streetPlaceholder: '55 Rue du Faubourg Saint-Honoré', cityPlaceholder: 'Paris', phonePlaceholder: '+33 1 42 92 81 00',
  },
  ZA: {
    name: 'South Africa', iso: 'za', subdivLabel: 'Province',
    subdivisions: ['Eastern Cape','Free State','Gauteng','KwaZulu-Natal','Limpopo','Mpumalanga','North West','Northern Cape','Western Cape'],
    postalLabel: 'Postal code', postalFormat: '4 digits', postalPlaceholder: '8001',
    streetPlaceholder: '1 Adderley Street', cityPlaceholder: 'Cape Town', phonePlaceholder: '+27 21 555 0100',
  },
  OTHER: {
    name: 'Other country', iso: 'un', subdivLabel: 'Region or state (optional)',
    subdivisions: null,
    postalLabel: 'Postal code', postalFormat: 'Any format', postalPlaceholder: 'Enter postal code',
    streetPlaceholder: 'Street and number', cityPlaceholder: 'City or locality', phonePlaceholder: '+XX phone',
  },
};